<template>
  <div class="import-core-ui">
    <div class="import-core-ui__form">
      <div>
        <ImportForm
          :resolver="resolver"
          :variant="variant"
          :header-indexes="headerIndexes"
          :selected-source-name="selectedSourceName"
          :lines-to-skip-count="Number(linesToSkipCount)"
          :is-submit-blocked="isSubmitBlocked"
          :class="formVariationClassName"
          @file-selected="setSelectedFile"
          @on-validation-error="handleValidationErrors"
          @on-request-error="handleRequestErrors"
          @on-submit="resetErrors"
        />
        <div class="import-core-ui__controls">
          <div class="import-core-ui__mapping-variant">
            <VHeading class="import-control" :level="4">Mapping layout</VHeading>
            <ul class="import-core-ui__variants">
              <li class="import-core-ui__variant">
                <VRadio
                  v-model="variant"
                  :value="EMappingVariant.UPRN"
                  label="UPRN"
                  name="variant"
                  :disabled="isConfigDisabled"
                />
              </li>
              <li class="import-core-ui__variant">
                <VRadio
                  v-model="variant"
                  :value="EMappingVariant.SINGLE_ADDRESS"
                  label="Single Field Address"
                  name="variant"
                  :disabled="isConfigDisabled"
                />
              </li>
              <li class="import-core-ui__variant">
                <VRadio
                  v-model="variant"
                  :value="EMappingVariant.MULTI_ADDRESS"
                  label="Multi Field Address"
                  name="variant"
                  :disabled="isConfigDisabled"
                />
              </li>
            </ul>
          </div>
          <div>
            <div class="import-core-ui__source">
              <VHeading class="import-control" :level="4">Source</VHeading>
              <ul class="import-core-ui__source-types">
                <li class="import-core-ui__source-type">
                  <VRadio
                    v-model="sourceType"
                    :value="ESourceType.NONE"
                    label="No source"
                    name="source-type"
                    :disabled="isConfigDisabled"
                  />
                  <VFallbackText class="hint import-core-ui__source-hint">
                    If you are uploading data from different CRMs or formats, make sure to assign a
                    unique source to each. Otherwise, client IDs may collide and overwrite existing
                    data.
                  </VFallbackText>
                </li>
                <li class="import-core-ui__source-type">
                  <VRadio
                    v-model="sourceType"
                    :value="ESourceType.FROM_CSV"
                    label="Source from imported CSV"
                    name="source-type"
                    :disabled="isConfigDisabled"
                  />
                  <VFallbackText
                    v-if="sourceType === ESourceType.FROM_CSV && !hasSourceColumn"
                    class="hint import-core-ui__source-error"
                  >
                    No source column found in the CSV mapping. Please map a column to "Source".
                  </VFallbackText>
                </li>
                <li class="import-core-ui__source-type">
                  <VRadio
                    v-model="sourceType"
                    :value="ESourceType.SINGLE"
                    label="Single source"
                    name="source-type"
                    :disabled="isConfigDisabled"
                  />
                </li>
              </ul>
              <div
                v-if="
                  sourceType === ESourceType.SINGLE &&
                  companyId !== undefined &&
                  companySourceResolver
                "
                class="import-core-ui__source-dropdown"
              >
                <ImportSource
                  :model-value="selectedSourceId"
                  :resolver="companySourceResolver"
                  :company-id="companyId"
                  :is-disabled="isConfigDisabled"
                  @select="handleSourceSelect"
                />
              </div>
            </div>
            <div v-show="variant === EMappingVariant.UPRN" class="import-core-ui__customer-ref">
              <VHeading class="import-control" :level="4">Customer reference</VHeading>
              <div class="import-core-ui__customer-ref-checkbox">
                <VCheckbox
                  v-model="isCustomerRefReplaced"
                  label="Replace with UPRN"
                  :disabled="isConfigDisabled"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="import-core-ui__config">
        <div v-if="companyId" class="import-core-ui__map-config">
          <ImportMap
            :company-id="companyId"
            :selected-map="selectedMap"
            :header-indexes="headerIndexes"
            :variant="variant"
            :source-type="sourceType"
            :selected-source-id="selectedSourceId"
            :selected-source-name="selectedSourceName"
            :is-disabled="isConfigDisabled"
            @on-select="setSelectedMap"
          />
        </div>
        <VFormControlGroup>
          <div class="import-core-ui__lines-config">
            <VInput
              v-model="linesToSkipCount"
              :config="{ label: 'Lines to skip', type: 'number', min: 0 }"
            />
            <div class="import-core-ui__config-hint">
              <VFallbackText class="hint">
                This number indicates how many of the first lines of your CSV file should be skipped
                when importing the data. For instance set it to 1 if the first row of your file
                contains headers.
              </VFallbackText>
            </div>
          </div>
        </VFormControlGroup>
      </div>
    </div>
    <div class="import-core-ui__errors">
      <ImportErrorCard v-if="formattedErrors.length" :list="formattedErrors" />
    </div>
    <ImportPreview
      :file="selectedFile"
      :selected-map="selectedMap"
      :variant="variant"
      :is-customer-ref-replaced="isCustomerRefReplaced"
      @index-updated="handleIndexUpdate"
      @set-is-customer-ref-replaced="setIsCustomerRefReplaced"
    />
  </div>
</template>

<script lang="ts" setup>
  // Types
  import type {
    IImportModel,
    TImportColumnIndexes,
    TImportFormattedError,
    TImportValidationError,
    TImportRequestErrors,
    IImportErrorFormatter,
  } from '~/shared/import/types';
  import type { TImportMap } from '~/shared/import/map/types';
  import type { ICompanySourceModel, TCompanySource } from '~/shared/source/types';
  import { EImportParamKey, EMappingVariant, ESourceType } from '~/shared/import/types';

  // Config
  import { DEFAULT_MAPPING_VARIANT, DEFAULT_SOURCE_TYPE } from '~/shared/import/config';

  // Services
  import { ImportRequestErrorFormatter } from '~/shared/import/error/service/formatter/request';
  import { ImportValidationErrorFormatter } from '~/shared/import/error/service/formatter/validation';

  // Components
  import ImportForm from '~/shared/import/form/ImportForm.vue';
  import ImportErrorCard from '~/shared/import/error/card/ImportErrorCard.vue';
  import ImportPreview from '~/shared/import/preview/ImportPreview.vue';
  import VFormControlGroup from '~/misc/v-form-control-group';
  import VInput from '~/misc/v-input';
  import VFallbackText from '~/misc/v-fallback-text/VFallbackText.vue';
  import ImportMap from '~/shared/import/map/ImportMap.vue';
  import VRadio from '~/misc/v-radio';
  import ImportSource from '~/shared/import/source/ImportSource.vue';
  import VHeading from '~/misc/v-heading/VHeading.vue';
  import VCheckbox from '~/misc/v-checkbox';

  const validationErrorFormatter: IImportErrorFormatter = new ImportValidationErrorFormatter();
  const requestErrorFormatter: IImportErrorFormatter = new ImportRequestErrorFormatter();

  defineProps({
    resolver: {
      type: Object as PropType<IImportModel>,
      required: true,
    },
    companySourceResolver: {
      type: Object as PropType<ICompanySourceModel>,
      default: undefined,
    },
    companyId: {
      type: Number,
      default: undefined,
    },
    formVariationClassName: {
      type: String,
      default: '',
    },
  });

  const selectedFile: Ref<File | null> = ref<File | null>(null);
  const headerIndexes: Ref<TImportColumnIndexes> = ref<TImportColumnIndexes>({});
  const linesToSkipCount: Ref<number> = ref<number>(1);
  const isSelectingMap: Ref<boolean> = ref<boolean>(false);
  const selectedMap: Ref<TImportMap | null> = ref<TImportMap | null>(null);
  const variant: Ref<EMappingVariant> = ref<EMappingVariant>(
    selectedMap.value?.column_layout ?? DEFAULT_MAPPING_VARIANT
  );
  const sourceType: Ref<ESourceType> = ref<ESourceType>(
    selectedMap.value?.source_type ?? DEFAULT_SOURCE_TYPE
  );
  const selectedSourceId: Ref<number | null> = ref<number | null>(null);
  const selectedSourceName: Ref<string | null> = ref<string | null>(null);
  const isCustomerRefReplaced: Ref<boolean> = ref<boolean>(false);
  const formattedErrors: Ref<TImportFormattedError[]> = ref<TImportFormattedError[]>([]);

  const isConfigDisabled: ComputedRef<boolean> = computed<boolean>(() => !selectedFile.value);

  const hasSourceColumn: ComputedRef<boolean> = computed<boolean>(
    () => EImportParamKey.SOURCE in headerIndexes.value
  );

  const isSubmitBlocked: ComputedRef<boolean> = computed<boolean>(() => {
    if (sourceType.value === ESourceType.FROM_CSV) return !hasSourceColumn.value;

    if (sourceType.value === ESourceType.SINGLE) return !selectedSourceId.value;

    return false;
  });

  const setSelectedFile = (file: File | null) => {
    selectedFile.value = file;
  };

  const setIsSelectingMap = (state: boolean) => {
    isSelectingMap.value = state;
  };

  const setSelectedMap = (map: TImportMap | null | undefined) => {
    selectedMap.value = map ?? null;
  };

  const setVariant = (value: EMappingVariant) => {
    variant.value = value;
  };

  const setSourceType = (type: ESourceType) => {
    sourceType.value = type;
  };

  const setSelectedSourceId = (id: number | null) => {
    selectedSourceId.value = id;
  };

  const setSelectedSourceName = (name: string | null) => {
    selectedSourceName.value = name;
  };

  const setFormattedErrors = (errors: TImportFormattedError[]) => {
    formattedErrors.value = [...errors];
  };

  const resetSelectedSource = () => {
    setSelectedSourceId(null);
    setSelectedSourceName(null);
  };

  const handleIndexUpdate = (data: TImportColumnIndexes) => {
    if (!isSelectingMap.value) {
      setSelectedMap(null);
    }

    headerIndexes.value = { ...data };
  };

  const handleSourceSelect = (source: TCompanySource | null) => {
    setSelectedSourceId(source?.id ?? null);
    setSelectedSourceName(source?.name ?? null);
  };

  const setIsCustomerRefReplaced = (state: boolean) => {
    isCustomerRefReplaced.value = state;
  };

  const resetErrors = () => setFormattedErrors([]);

  const handleValidationErrors = (errors: TImportValidationError[]) =>
    setFormattedErrors([...formattedErrors.value, ...validationErrorFormatter.process(errors)]);

  const handleRequestErrors = (errors: TImportRequestErrors) =>
    setFormattedErrors([...formattedErrors.value, ...requestErrorFormatter.process(errors)]);

  watch(selectedFile, resetErrors);

  watch(
    () => selectedMap.value,
    async (map: TImportMap | null) => {
      if (!map) {
        setIsSelectingMap(false);

        return;
      }

      setIsSelectingMap(true);
      setVariant(map.column_layout ?? DEFAULT_MAPPING_VARIANT);
      setSourceType(map.source_type ?? DEFAULT_SOURCE_TYPE);
      setSelectedSourceId(map.source_id != null ? Number(map.source_id) : null);
      setSelectedSourceName(map.source_name ?? null);

      await nextTick();

      setIsSelectingMap(false);
    }
  );

  watch(sourceType, (type: ESourceType) => {
    if (type !== ESourceType.SINGLE) {
      resetSelectedSource();
    }

    if (isSelectingMap.value) return;

    setSelectedMap(null);
  });

  watch(selectedSourceId, () => {
    if (isSelectingMap.value) return;

    setSelectedMap(null);
  });
</script>
